/**
 * EditarReservaSheet — Modal de edición de reserva existente.
 *
 * Usado desde:
 *   - Muelle (DetalleSheet → botón "Editar")
 *   - Booking (ReservaSlideOver → botón "Editar")
 *
 * El backend (`PATCH /turismo/reservaciones/{id}/editar`) recalcula precio,
 * comisión y saldo automáticamente cuando cambia salida/pax/agencia, y ajusta
 * los cupos de bote en las salidas implicadas. La auditoría queda con before/
 * after y el motivo que el operador escriba.
 *
 * Guards (los aplica también el backend, los duplicamos en el caller para
 * mostrar tooltip explicativo sin roundtrip):
 *   - Creada el mismo día calendario.
 *   - estado_embarque != 'embarcado'.
 *   - estado != 'cancelada'.
 *   - Sin liquidación de comisión.
 */
import React from 'react';
import {
  Box, Typography, TextField, MenuItem, Button, Alert,
} from '@mui/material';
import BottomSheet from '../common/BottomSheet';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.company_id || u.empresa_id || '1'; }
  catch { return '1'; }
}
function getUserId() {
  try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.id || u.user_id || null; }
  catch { return null; }
}
function getUserName() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const partes = [u.name, u.lastname].filter(Boolean);
    return partes.join(' ').trim() || u.email || null;
  } catch { return null; }
}
function getAuthHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

export default function EditarReservaSheet({ reserva, onClose, onSuccess, onError }) {
  const empresaId = getEmpresaId();
  const [form, setForm] = React.useState(null);
  const [salidas, setSalidas] = React.useState([]);
  const [agencias, setAgencias] = React.useState([]);
  const [saving, setSaving] = React.useState(false);

  // Inicializar / resetear el form al abrir. Mapea ambos formatos de campos
  // (los del muelle/dia: telefono/observaciones; los del slide-over de booking:
  // numero_whatsapp/observaciones_agencia).
  React.useEffect(() => {
    if (!reserva) { setForm(null); return; }
    setForm({
      nombre_cliente: reserva.nombre_cliente || '',
      numero_whatsapp: reserva.telefono || reserva.numero_whatsapp || '',
      correo: reserva.correo || '',
      hotel_huesped: reserva.hotel || reserva.hotel_huesped || '',
      salida_id: String(reserva._salida?.salida_id || reserva.salida_id || reserva.salida?.id || ''),
      adultos: reserva.adultos || 0,
      ninos: reserva.ninos || 0,
      bebes: reserva.bebes || 0,
      cortesias_adultos: reserva.cortesias_adultos || 0,
      cortesias_ninos: reserva.cortesias_ninos || 0,
      agencia_id: reserva.agencia_id != null ? String(reserva.agencia_id) : '',
      observaciones_agencia: reserva.observaciones || reserva.observaciones_agencia || '',
      tiquete_agencia: reserva.tiquete_agencia || '',
      motivo_edicion: '',
    });
  }, [reserva]);

  React.useEffect(() => {
    if (!reserva) return;
    fetch(`${API_BASE}/turismo/salidas?empresa_id=${empresaId}&solo_activas=true`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(setSalidas).catch(() => setSalidas([]));
    fetch(`${API_BASE}/turismo/agencias?empresa_id=${empresaId}&solo_activas=true`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(setAgencias).catch(() => setAgencias([]));
  }, [reserva, empresaId]);

  if (!reserva || !form) return null;

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const body = {
        nombre_cliente: form.nombre_cliente.trim(),
        numero_whatsapp: form.numero_whatsapp.trim(),
        correo: form.correo.trim() || null,
        hotel_huesped: form.hotel_huesped.trim() || null,
        salida_id: form.salida_id ? parseInt(form.salida_id) : null,
        adultos: Number(form.adultos) || 0,
        ninos: Number(form.ninos) || 0,
        bebes: Number(form.bebes) || 0,
        cortesias_adultos: Number(form.cortesias_adultos) || 0,
        cortesias_ninos: Number(form.cortesias_ninos) || 0,
        agencia_id: form.agencia_id === '' ? 0 : parseInt(form.agencia_id),  // 0 = venta directa
        observaciones_agencia: form.observaciones_agencia.trim() || null,
        tiquete_agencia: form.tiquete_agencia.trim() || null,
        motivo_edicion: form.motivo_edicion.trim() || null,
        usuario_id: getUserId(),
        usuario_nombre: getUserName(),
        expected_version: reserva.version,
      };
      const r = await fetch(`${API_BASE}/turismo/reservaciones/${reserva.id}/editar`, {
        method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        const e = new Error(err.detail || 'Error');
        e.status = r.status;
        throw e;
      }
      const data = await r.json();
      onSuccess(data);
    } catch (e) { onError(e); }
    setSaving(false);
  };

  return (
    <BottomSheet open={!!reserva} onClose={onClose} title={`Editar ${reserva.codigo_reserva}`}>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Alert severity="info" sx={{ fontSize: 11.5 }}>
          Al cambiar salida o pasajeros se recalcula precio, comisión y saldo.
          Los cupos de los botes se ajustan automáticamente.
        </Alert>

        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', mt: 0.5 }}>
          Titular
        </Typography>
        <TextField size="small" label="Nombre cliente *" value={form.nombre_cliente}
          onChange={e => setForm({ ...form, nombre_cliente: e.target.value })} />
        <TextField size="small" label="WhatsApp" value={form.numero_whatsapp}
          onChange={e => setForm({ ...form, numero_whatsapp: e.target.value })} />
        <TextField size="small" label="Correo" value={form.correo}
          onChange={e => setForm({ ...form, correo: e.target.value })} />
        <TextField size="small" label="Hotel donde se hospeda" value={form.hotel_huesped}
          onChange={e => setForm({ ...form, hotel_huesped: e.target.value })} />

        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', mt: 1 }}>
          Salida (pasadía)
        </Typography>
        <TextField size="small" select label="Pasadía"
          value={form.salida_id}
          onChange={e => setForm({ ...form, salida_id: e.target.value })}>
          {salidas.map(s => (
            <MenuItem key={s.id} value={String(s.id)}>
              {s.fecha?.slice(0, 10)} · {s.paquete?.nombre || s.paquete_nombre || `Salida #${s.id}`} · {s.hora_salida ? s.hora_salida.slice(0, 5) : ''}
            </MenuItem>
          ))}
        </TextField>

        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', mt: 1 }}>
          Pasajeros
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
          <TextField size="small" type="number" label="Adultos" value={form.adultos}
            onChange={e => setForm({ ...form, adultos: e.target.value })} inputProps={{ min: 0 }} />
          <TextField size="small" type="number" label="Niños" value={form.ninos}
            onChange={e => setForm({ ...form, ninos: e.target.value })} inputProps={{ min: 0 }} />
          <TextField size="small" type="number" label="Bebés" value={form.bebes}
            onChange={e => setForm({ ...form, bebes: e.target.value })} inputProps={{ min: 0 }} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
          <TextField size="small" type="number" label="Cortesías adultos" value={form.cortesias_adultos}
            onChange={e => setForm({ ...form, cortesias_adultos: e.target.value })} inputProps={{ min: 0 }} />
          <TextField size="small" type="number" label="Cortesías niños" value={form.cortesias_ninos}
            onChange={e => setForm({ ...form, cortesias_ninos: e.target.value })} inputProps={{ min: 0 }} />
        </Box>

        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', mt: 1 }}>
          Comercialización
        </Typography>
        <TextField size="small" select label="Agencia"
          value={form.agencia_id}
          onChange={e => setForm({ ...form, agencia_id: e.target.value })}>
          <MenuItem value="">— Venta directa (sin agencia) —</MenuItem>
          {agencias.map(a => (
            <MenuItem key={a.id} value={String(a.id)}>{a.nombre}</MenuItem>
          ))}
        </TextField>
        <TextField size="small" label="Tiquete agencia" value={form.tiquete_agencia}
          onChange={e => setForm({ ...form, tiquete_agencia: e.target.value })} />
        <TextField size="small" multiline minRows={2} label="Observaciones agencia"
          value={form.observaciones_agencia}
          onChange={e => setForm({ ...form, observaciones_agencia: e.target.value })} />

        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', mt: 1 }}>
          Motivo de la edición
        </Typography>
        <TextField size="small" multiline minRows={2}
          label="Por qué se está editando (queda en auditoría)"
          value={form.motivo_edicion}
          onChange={e => setForm({ ...form, motivo_edicion: e.target.value })} />

        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button fullWidth onClick={onClose} disabled={saving}
            sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancelar
          </Button>
          <Button fullWidth variant="contained" disableElevation
            onClick={handleSubmit} disabled={saving || !form.nombre_cliente.trim()}
            sx={{ textTransform: 'none', fontWeight: 700,
                   background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </Box>
      </Box>
    </BottomSheet>
  );
}
